import { ReactComponent as IconChewronDown } from './img/iconChevronDown.svg';
import { Modal } from 'components/Modal';
import { FC, useState } from 'react';
import styles from './ModalAddingDocument.module.scss';
import { Title } from 'components/Title';
import { Label } from 'components/CreateOrderForm';
import { Loader, Select } from '@mantine/core';
import { Button } from 'ui/Button';
import {
  DocumentType,
  ModalAddingDocumentProps,
  TypesDocumentsErrors,
  TypesDocumentsValues,
} from './type';
import { selectStyles, typesDocuments, visaTypes } from './constants';
import { getEmptyErrors, getEmptyValues } from './utils';
import { DateField, SelectField, TextField } from './fields';
import { useAddDocumentMutation } from 'redux/Api/employedsWorkSupportApi/employedsWorkSupportApi';

export const ModalAddingDocument: FC<ModalAddingDocumentProps> = ({
  setShow,
  show,
  workerId,
  onSuccess,
}) => {
  const [documentType, setDocumentType] = useState<DocumentType>('residence_permit');
  const [addDocument, { isLoading }] = useAddDocumentMutation();
  const [values, setValues] = useState<TypesDocumentsValues>(
    getEmptyValues('residence_permit'),
  );
  const [error, setError] = useState<TypesDocumentsErrors>(getEmptyErrors());
  const [errorMessage, setErrorMessage] = useState('');

  const handleChangeDocumentType = (val: string | null) => {
    if (!val) return;
    const type = val as DocumentType;
    setDocumentType(type);
    setValues(getEmptyValues(type));
    setError(getEmptyErrors());
    setErrorMessage('');
  };

  const changeField = (field: keyof TypesDocumentsValues, value: string) => {
    setValues({ ...values, [field]: value });
    setError({ ...error, [field]: '' });
    setErrorMessage('');
  };

  const renderResidencePermit = () => (
    <div className={styles.fields}>
      <TextField
        label='Серия документа (ровно 2 цифры)'
        placeholder='Введи серию документа'
        value={values.series || ''}
        onChange={(val) => changeField('series', val)}
        error={error.series}
        required
      />
      <TextField
        label='Номер документа (ровно 7 цифр)'
        placeholder='Введи номер документа'
        value={values.number || ''}
        onChange={(val) => changeField('number', val)}
        error={error.number}
        required
      />
      <TextField
        label='Орган, выдавший документ'
        placeholder='Введи орган, выдавший документ'
        value={values.issuer || ''}
        onChange={(val) => changeField('issuer', val)}
        error={error.issuer}
        required
      />
      <div className={styles.containerDate}>
        <DateField
          label='Дата выдачи документа'
          value={values.issue_date || ''}
          onChange={(val) => changeField('issue_date', val)}
          error={error.issue_date}
        />
        <DateField
          label='Дата окончания действия документа'
          value={values.validity_until || ''}
          onChange={(val) => changeField('validity_until', val)}
          error={error.validity_until}
        />
      </div>
    </div>
  );

  const renderVisa = () => (
    <div className={styles.fields}>
      <SelectField
        label='Тип визы'
        placeholder='Выбери тип визы'
        data={visaTypes}
        value={values.visa_type || ''}
        onChange={(val) => changeField('visa_type', val)}
        error={error.visa_type}
      />
      {values.visa_type === 'physical_visa' && (
        <TextField
          label='Серия документа (2 цифры)'
          placeholder='Введи серию документа'
          value={values.series || ''}
          onChange={(val) => changeField('series', val)}
          error={error.series}
        />
      )}
      <TextField
        label='Номер документа'
        placeholder='Введи номер документа'
        value={values.number || ''}
        onChange={(val) => changeField('number', val)}
        error={error.number}
        required
      />
      {values.visa_type === 'e_visa' && (
        <TextField
          label='Срок пребывания (дни)'
          placeholder='Введи срок пребывания'
          value={values.duration_of_stay || ''}
          onChange={(val) => changeField('duration_of_stay', val)}
          error={error.duration_of_stay}
        />
      )}
      <div className={styles.containerDate}>
        <DateField
          label='Дата начала действия документа'
          value={values.validity_from || ''}
          onChange={(val) => changeField('validity_from', val)}
          error={error.validity_from}
        />
        <DateField
          label='Дата окончания действия документа'
          value={values.validity_until || ''}
          onChange={(val) => changeField('validity_until', val)}
          error={error.validity_until}
        />
      </div>
    </div>
  );

  const renderMigrationCard = () => (
    <div className={styles.fields}>
      <TextField
        label='Серия документа (4 цифры)'
        placeholder='Введи серию документа'
        value={values.series || ''}
        onChange={(val) => changeField('series', val)}
        error={error.series}
        required
      />
      <TextField
        label='Номер документа (от 4 до 9 цифр)'
        placeholder='Введи номер документа'
        value={values.number || ''}
        onChange={(val) => changeField('number', val)}
        error={error.number}
        required
      />
      <div className={styles.containerDate}>
        <DateField
          label='Дата выдачи документа'
          value={values.issue_date || ''}
          onChange={(val) => changeField('issue_date', val)}
          error={error.issue_date}
        />
        <DateField
          label='Дата окончания действия документа'
          value={values.validity_until || ''}
          onChange={(val) => changeField('validity_until', val)}
          error={error.validity_until}
        />
      </div>
    </div>
  );

  const renderTemporaryResidencePermit = () => (
    <div className={styles.fields}>
      <TextField
        label='Орган, выдавший документ'
        placeholder='Введи орган, выдавший документ'
        value={values.issuer || ''}
        onChange={(val) => changeField('issuer', val)}
        error={error.issuer}
        required
      />
      <TextField
        label='Номер документа'
        placeholder='Введи номер документа'
        value={values.number || ''}
        onChange={(val) => changeField('number', val)}
        error={error.number}
      />
      <div className={styles.containerDate}>
        <DateField
          label='Дата выдачи документа'
          value={values.issue_date || ''}
          onChange={(val) => changeField('issue_date', val)}
          error={error.issue_date}
        />
      </div>
    </div>
  );

  const renderPatent = () => (
    <div className={styles.fields}>
      <TextField
        label='Серия документа (2 цифры)'
        placeholder='Введи серию документа'
        value={values.series || ''}
        onChange={(val) => changeField('series', val)}
        error={error.series}
        required
      />
      <TextField
        label='Номер документа (от 4 до 10 цифр)'
        placeholder='Введи номер документа'
        value={values.number || ''}
        onChange={(val) => changeField('number', val)}
        error={error.number}
        required
      />
      <TextField
        label='Орган, выдавший документ'
        placeholder='Введи орган, выдавший документ'
        value={values.issuer || ''}
        onChange={(val) => changeField('issuer', val)}
        error={error.issuer}
        required
      />
      <div className={styles.containerDate}>
        <DateField
          label='Дата выдачи документа'
          value={values.issue_date || ''}
          onChange={(val) => changeField('issue_date', val)}
          error={error.issue_date}
        />
      </div>
    </div>
  );

  const renderWorkPermit = () => (
    <div className={styles.fields}>
      <TextField
        label='Примечания'
        placeholder='Введи примечание'
        value={values.annotations || ''}
        onChange={(val) => changeField('annotations', val)}
        error={error.annotations}
        required
      />
      <TextField
        label='Серия документа (2 цифры)'
        placeholder='Введи серию документа'
        value={values.series || ''}
        onChange={(val) => changeField('series', val)}
        error={error.series}
        required
      />
      <TextField
        label='Номер документа (10 цифр)'
        placeholder='Введи номер документа'
        value={values.number || ''}
        onChange={(val) => changeField('number', val)}
        error={error.number}
        required
      />
      <TextField
        label='Орган, выдавший документ'
        placeholder='Введи орган, выдавший документ'
        value={values.issuer || ''}
        onChange={(val) => changeField('issuer', val)}
        error={error.issuer}
        required
      />
      <div className={styles.containerDate}>
        <DateField
          label='Дата выдачи документа'
          value={values.issue_date || ''}
          onChange={(val) => changeField('issue_date', val)}
          error={error.issue_date}
        />
      </div>
    </div>
  );
  console.log(error);
  const renderEmploymentContract = () => (
    <div className={styles.fields}>
      <TextField
        label='Номер документа'
        placeholder='Введи номер документа'
        value={values.number || ''}
        onChange={(val) => changeField('number', val)}
        error={error.number}
      />
      <div className={styles.containerDate}>
        <DateField
          label='Дата выдачи документа'
          value={values.issue_date || ''}
          onChange={(val) => changeField('issue_date', val)}
          error={error.issue_date}
        />
        <DateField
          label='Дата окончания действия документа'
          value={values.validity_until || ''}
          onChange={(val) => changeField('validity_until', val)}
          error={error.validity_until}
        />
      </div>
    </div>
  );

  const renderFieldsByType = () => {
    switch (documentType) {
      case 'residence_permit':
        return renderResidencePermit();
      case 'visa':
        return renderVisa();
      case 'migration_card':
        return renderMigrationCard();
      case 'temporary_residence_permit':
        return renderTemporaryResidencePermit();
      case 'patent':
        return renderPatent();
      case 'work_permit':
        return renderWorkPermit();
      case 'employment_contract':
        return renderEmploymentContract();
      default:
        return null;
    }
  };

  const handleAddDocument = async () => {
    try {
      await addDocument({
        id: workerId,
        body: { ...values, type: documentType },
      }).unwrap();
      setError(getEmptyErrors());
      setErrorMessage('');
      setShow(false);
      onSuccess();
    } catch (err) {
      console.error(err);
      const data = (
        err as { data?: { errors?: { [key: string]: string[] }; message?: string } }
      ).data;
      const errorData = data?.errors;

      if (!errorData && data?.message) {
        setErrorMessage(data.message);
      }

      if (errorData) {
        const nextErrors = getEmptyErrors();
        (Object.keys(nextErrors) as (keyof TypesDocumentsErrors)[]).forEach((key) => {
          nextErrors[key] = errorData[key]?.[0] || '';
        });
        setError(nextErrors);
      }
    }
  };

  return (
    <Modal setOpen={setShow} open={show}>
      <div className={styles.root}>
        <div>
          <Title title='Добавить документ' />
        </div>

        <div>
          <Label text='Тип документа' />
          <Select
            data={typesDocuments || []}
            nothingFoundMessage='Ничего не найдено'
            placeholder='Выбери тип документа'
            styles={{
              ...selectStyles,
              input: {
                ...selectStyles.input,
                border: error.type ? '1px solid red' : 'none',
              },
            }}
            comboboxProps={{
              position: 'bottom',
              middlewares: { flip: false, shift: false },
              offset: 4,
            }}
            value={documentType ?? null}
            onChange={handleChangeDocumentType}
            withCheckIcon={false}
            rightSection={<IconChewronDown />}
            allowDeselect={false}
          />
          {error.type && <div className={styles.error}>{error.type}</div>}
        </div>

        {renderFieldsByType()}

        <div style={{ marginTop: '24px' }}>
          <Button theme='filed' disabled={isLoading} onClick={handleAddDocument}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Добавить документ {isLoading && <Loader size='xs' color='#fff' />}
            </div>
          </Button>
          {errorMessage && <div className={styles.error}>{errorMessage}</div>}
        </div>
      </div>
    </Modal>
  );
};
